import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { Grid, Button, Form, Input, Message, Typography, Link, Divider } from "@arco-design/web-react";
import axios from "axios";

import { endpoints } from "../../api/endpoints";
import i18n from "../../i18n.config";
import { Logo } from "../../components/Logo";
import { GoogleAuthButton } from "../../components/GoogleAuthButton";
import { MicrosoftLoginButton } from "../../components/MicrosoftLoginButton";
import { LanguageSelect } from "../../components/LanguageSelect/LanguageSelect";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { clearUser, setUser } from "../../store/userSlice";
import { getBoxBackgroundColor, getButtonColor, getButtonTextColor, getColorAccent1, getTextColor } from "../../utils/cssVariables";
import { getAuthLogoType } from "../../theme";
import { isQrRegistrationSource, QR_REGISTRATION_SOURCE } from "../../constants/qrRegistration";
import { completeAuthRedirect, shouldDeferAutoAuthRedirect } from "../../utils/authReturnUrl";
import { defaultAuthenticatedPath } from "../../utils/defaultAuthenticatedPath";
import { useEventIntakeEnabled } from "../../hooks/useEventIntakeEnabled";
import { isBookMeetingIntent, registerPathWithBookMeetingIntent } from "../../constants/authBookMeeting";
import { registrationAttributionFromSearchParams } from "../../constants/guestIntake";
import { BookMeetingAuthModal } from "../../components/BookMeetingAuthModal/BookMeetingAuthModal";


const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;

export const LoginLegacy = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);
  const qrSignupEnabled = useSelector((state: RootState) => state.config.qrSignupEnabled);
  const brandName = useSelector((state: RootState) => state.config.brandName);
  // Re-compute when API theme lands so auth logo switches from Main → tenant brand.
  const themeKey = useSelector((state: RootState) => state.config.themeKey);
  const authLogoType = useMemo(() => getAuthLogoType(), [themeKey]);
  const [searchParams] = useSearchParams();
  const registrationSource = searchParams.get('source');
  const intent = searchParams.get('intent');
  const isBookMeetingFlow = isBookMeetingIntent(intent);
  const intakeAttribution = registrationAttributionFromSearchParams(searchParams);
  const registerHref = isQrRegistrationSource(registrationSource)
    ? `${routes.REGISTER}?source=${QR_REGISTRATION_SOURCE}`
    : isBookMeetingFlow
      ? registerPathWithBookMeetingIntent(intakeAttribution)
      : routes.REGISTER;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eventIntakeEnabled = useEventIntakeEnabled();
  const configLoaded = useSelector((state: RootState) => state.config.configLoaded);
  const [form] = Form.useForm();
  const [bookMeetingModalOpen, setBookMeetingModalOpen] = useState(isBookMeetingFlow);

  useEffect(() => {
    if (user === null || shouldDeferAutoAuthRedirect() || !configLoaded) {
      return;
    }
    const defaultPage = defaultAuthenticatedPath(user, eventIntakeEnabled, searchParams);
    completeAuthRedirect(defaultPage);
  }, [user, eventIntakeEnabled, searchParams, configLoaded]);

  if (isQrRegistrationSource(registrationSource) && !qrSignupEnabled) {
    return (
      <div
        style={{
          justifyContent: "center",
          backgroundColor: getBoxBackgroundColor(),
          maxWidth: 480,
          width: "calc(100% - 40px)",
          margin: "20px auto",
          padding: "20px",
          borderRadius: 8,
        }}
      >
        <Logo variant="auth" style={{ marginTop: 10 }} type={authLogoType} />
        <Typography.Title heading={4} style={{ marginTop: 24, textAlign: 'center' }}>
          Event registration unavailable
        </Typography.Title>
        <Typography.Text style={{ display: 'block', marginTop: 16, textAlign: 'center' }}>
          QR registration is not enabled for this organization.
        </Typography.Text>
      </div>
    );
  }

  const onSubmit = async () => {
    if (form) {
      form.validate().then(async () => {
        const email = form.getFieldValue("email");
        const password = form.getFieldValue("password");
        const auth = getAuth();
        try {
          dispatch(clearUser());

          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const firebaseUser = userCredential.user;
          const idToken = await firebaseUser.getIdToken()
          const response = await axios.get(endpoints.whoami, {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          dispatch(setUser({ ...response.data }));
          completeAuthRedirect(defaultAuthenticatedPath(response.data, eventIntakeEnabled, searchParams));
        } catch (error) {
          console.error("Login failed:", error);
          Message.error(t('notification_messages.logged_in_error'));
          localStorage.removeItem("user");
        }
      });
    }
  };

  return (
    <div
      style={{
        justifyContent: "center",
        backgroundColor: getBoxBackgroundColor(),
        maxWidth: 480,
        width: "calc(100% - 40px)",
        margin: "20px auto",
        padding: "20px",
        borderRadius: 8,
      }}
    >
      <Logo variant="auth" style={{ marginTop: 10 }} type={authLogoType} />
      <div style={{
        display: 'flex',
        justifyContent: "center",
        paddingTop: 12,
        margin: "0 auto",
      }}><LanguageSelect color={getTextColor()}/></div>
      <BookMeetingAuthModal
        visible={isBookMeetingFlow && bookMeetingModalOpen}
        brandName={brandName || 'WERULE'}
        onSignIn={() => setBookMeetingModalOpen(false)}
        onRegister={() => {
          setBookMeetingModalOpen(false);
          navigate(registerHref);
        }}
        onClose={() => setBookMeetingModalOpen(false)}
      />
      <Form onSubmit={onSubmit} form={form}>
        <Row justify="center">
          <div
            style={{
              padding: "20px",
              width: "calc(100% - 40px)",
              maxWidth: 350,
            }}
          >
            <div style={{ display: "grid", marginTop: 10 }}>
              <Typography.Text
                style={{
                  fontSize: 30,
                  fontWeight: 500,
                  color: getTextColor(),
                }}
                type="success"
                className={"alice-regular"}
              >
                {t('register_page.account_exists_message')}
              </Typography.Text>
              <Typography.Text
                style={{
                  fontSize: 24,
                  fontWeight: 500,
                  lineHeight: 1,
                  color: getTextColor(),
                }}
                type="success"
                className={"alice-regular"}
              >
                {t('login_page.login_below')}
              </Typography.Text>
            </div>
            <div style={{ marginTop: 40 }}>
              <FormItem
                id="email"
                field="email"
                layout="vertical"
                label={t('login_page.email_label')}
                rules={[{ required: true, message: t('login_page.email_placeholder') }, {
                  validator: (v, cb) => {
                    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                    if (!v || v.trim().length === 0) {
                      cb(t('form_validation_messages.empty_email'));
                    } else if (v.length > 100) {
                      cb(t('form_validation_messages.long_email'));
                    } else if (!emailRegex.test(v)) {
                      cb(t('form_validation_messages.invalid_email'));
                    } else {
                      cb();
                    }
                  },
                }]}
              >
                <Input placeholder={t('login_page.email_placeholder')} />
              </FormItem>
              <FormItem
                id="password"
                field="password"
                layout="vertical"
                label={t('login_page.password_label')}
                rules={[{ required: true, message: t('form_validation_messages.no_password') }, {
                  minLength: 6,
                  message: t('form_validation_messages.short_password'),
                }, {
                  validator: (v, cb) => {
                    if (!v || v.trim().length === 0) {
                      cb(t('form_validation_messages.invalid_password'));
                    } else {
                      cb();
                    }
                  },
                }]}
              >
                <Input.Password
                  type="password"
                  placeholder={t('login_page.password_placeholder')}
                />
              </FormItem>
              <div style={{ margin: '12px 0' }}>
                {t('register_page.agreement')}
                <Link
                  href={"https://we-rule.com/terms-conditions"}
                  target="_blank"
                >
                  {t('register_page.terms')}
                </Link>
              </div>
              <Button
                htmlType="submit"
                style={{
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                  width: "100%",
                }}
                size={"large"}
                shape={"round"}
              >
                {t('login_page.log_in_button')}
              </Button>
            </div>
            <Divider orientation="center">{t('login_page.or_text')}</Divider>
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <GoogleAuthButton />
            </div>
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <MicrosoftLoginButton />
            </div>
          </div>
        </Row>
      </Form>

      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Typography.Paragraph>
          <div style={{ display: "grid", marginTop: 10 }}>
            <Typography.Text
              style={{
                fontSize: 32,
                fontWeight: 500,
                lineHeight: 1,
                color: getTextColor(),
              }}
              type="success"
              className={"alice-regular"}
            >
              {t('login_page.no_account_message')}
            </Typography.Text>
            <Typography.Text
              style={{
                fontSize: 24,
                fontWeight: 500,
                lineHeight: 1,
                color: getTextColor(),
              }}
              type="success"
              className={"alice-regular"}
            >
              {t('login_page.register_below')}
            </Typography.Text>
            <NavLink to={registerHref}>
              <Button
                htmlType="submit"
                style={{
                  marginTop: 24,
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                  maxWidth: 350,
                  width: "100%",
                }}
                size={"large"}
                shape={"round"}
              >
                {t('login_page.sign_up_button')}
              </Button>
            </NavLink>
          </div>
          <br />
          {t('login_page.forgot_password')}
          <NavLink to={routes.RESETPASSWORD}>
            <Button
              style={{ padding: "0 0 0 4px" }}
              type="text"
              status="success"
            >
              <span style={{ color: getColorAccent1() }}>{t('login_page.reset_password')}</span>
            </Button>
          </NavLink>
        </Typography.Paragraph>
      </div>
    </div>
  );
};
